import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchaseOrdersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { HelpButton } from '../components/HelpButton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Plus, Search, FileText, Filter } from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor } from '../lib/utils';

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadPOs();
  }, []);

  const loadPOs = async () => {
    try {
      const response = await purchaseOrdersAPI.list();
      setPos(response.data);
    } catch (error) {
      console.error('Failed to load POs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPOs = pos.filter(po => {
    const matchesSearch = 
      po.po_number.toLowerCase().includes(search.toLowerCase()) ||
      po.vendor_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Purchase Orders</h1>
          <p className="text-slate-500 mt-1">Manage vendor purchase orders</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton module="purchase-orders" />
          <RequireCap module="purchase_orders" action="add">
            <Button onClick={() => navigate('/purchase-orders/new')} data-testid="new-po-btn">
              <Plus className="w-4 h-4 mr-2" />
              New PO
            </Button>
          </RequireCap>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search POs..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="po-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="po-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partially_received">Partially Received</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* PO Table */}
      <Card>
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 skeleton-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO Number</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Expected</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPOs.map(po => (
                <TableRow 
                  key={po.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/purchase-orders/${po.id}`)}
                  data-testid={`po-row-${po.id}`}
                >
                  <TableCell className="font-medium font-mono">{po.po_number}</TableCell>
                  <TableCell>{po.vendor_name}</TableCell>
                  <TableCell>{po.location_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{po.lines?.length || 0} items</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(po.total_amount)}
                  </TableCell>
                  <TableCell>{po.expected_date ? formatDate(po.expected_date) : '-'}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(po.status)}>
                      {po.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(po.created_at)}</TableCell>
                </TableRow>
              ))}
              {filteredPOs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No purchase orders found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
